import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { KeyRound } from 'lucide-react';
import FloatingSymbols from '../components/FloatingSymbols';
import BrandLogo from '../components/BrandLogo';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Password-reset landing page.
 *
 * Reads `?token=` from the reset-email link, verifies + updates the password
 * against the backend, then bounces the user to /login on success. Handles
 * missing/expired tokens with an inline error rather than a broken form.
 */
const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token') || '', [params]);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) toast.error('Reset link is missing its token — request a new one');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      setDone(true);
      toast.success('Password updated — sign in with your new password');
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed — please request a new link');
    } finally { setLoading(false); }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4 overflow-hidden">
      <FloatingSymbols opacity={0.9} />
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-6">
          <BrandLogo size={44} className="mb-2 justify-center w-full" />
          <p className="text-slate-600">Set a new password</p>
        </div>

        <Card className="border-slate-200 shadow-lg" data-testid="reset-password-card">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <KeyRound className="h-5 w-5 text-blue-800" />
              Reset Password
            </CardTitle>
            <CardDescription>Enter your new password — links expire an hour after they're sent.</CardDescription>
          </CardHeader>
          <CardContent>
            {!token ? (
              <div data-testid="reset-password-missing-token" className="text-sm space-y-3">
                <p className="text-slate-800">
                  This reset link is missing its token. It may have been truncated by your email client.
                </p>
                <Link to="/forgot-password" className="text-blue-800 hover:text-blue-900 font-medium" data-testid="reset-password-request-new-link">
                  Request a new reset link →
                </Link>
              </div>
            ) : done ? (
              <div data-testid="reset-password-done" className="text-sm text-slate-800">
                Password updated. Redirecting you to sign in…
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="rp-new">New password</Label>
                  <Input
                    id="rp-new"
                    type="password"
                    placeholder="At least 6 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    data-testid="reset-password-new-input"
                    autoComplete="new-password"
                  />
                  <PasswordStrengthMeter password={newPassword} testId="reset-password-strength" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rp-confirm">Confirm password</Label>
                  <Input
                    id="rp-confirm"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="reset-password-confirm-input"
                    autoComplete="new-password"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-blue-800 hover:bg-blue-900 text-white font-medium"
                  disabled={loading}
                  data-testid="reset-password-submit-button"
                >
                  {loading ? 'Updating…' : 'Update password'}
                </Button>
                <div className="text-center text-sm">
                  <Link to="/login" className="text-slate-600 hover:text-blue-800" data-testid="reset-password-cancel-link">
                    ← Back to sign in
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
